<template>
  <div class="commissions-page">
    <div class="commissions-heading">
      <div>
        <h1 class="mb-25">
          Comissões
        </h1>
        <p class="text-muted mb-0">
          Compare estruturas e consulte cada parcela sem perder o contexto.
        </p>
      </div>
      <div
        v-if="items.length"
        class="commissions-heading__stats"
        aria-label="Resumo das comissões"
      >
        <span><strong>{{ matrix.rows.length }}</strong> estruturas</span>
        <span><strong>{{ matrix.installments.length }}</strong> parcelas</span>
      </div>
    </div>

    <div
      v-if="loading"
      class="d-flex flex-column align-items-center justify-content-center p-5"
    >
      <b-spinner variant="primary" />
      <span class="text-muted mt-1">Organizando comissões…</span>
    </div>

    <template v-else-if="items.length > 0">
      <b-card
        no-body
        class="commission-card m-2 mb-0"
      >
        <div class="matrix-toolbar">
          <div
            class="matrix-legend"
            aria-label="Legenda"
          >
            <span><i class="legend-dot legend-dot--invoiced" /> Faturada</span>
            <span><i class="legend-dot legend-dot--pending" /> Não faturada</span>
          </div>
          <div
            class="matrix-navigation"
            aria-label="Navegação entre parcelas"
          >
            <span
              class="matrix-range"
              aria-live="polite"
            >{{ rangeLabel }}</span>
            <b-button
              variant="flat-secondary"
              size="sm"
              :disabled="isFirstWindow"
              aria-label="Ir para a primeira parcela"
              @click="goToFirst"
            ><feather-icon
              icon="ChevronsLeftIcon"
              size="17"
            /></b-button>
            <b-button
              variant="flat-secondary"
              size="sm"
              :disabled="isFirstWindow"
              aria-label="Ver parcelas anteriores"
              @click="previousWindow"
            ><feather-icon
              icon="ChevronLeftIcon"
              size="17"
            /></b-button>
            <b-button
              variant="flat-secondary"
              size="sm"
              :disabled="isLastWindow"
              aria-label="Ver próximas parcelas"
              @click="nextWindow"
            ><feather-icon
              icon="ChevronRightIcon"
              size="17"
            /></b-button>
            <b-button
              variant="flat-secondary"
              size="sm"
              :disabled="isLastWindow"
              aria-label="Ir para a última parcela"
              @click="goToLast"
            ><feather-icon
              icon="ChevronsRightIcon"
              size="17"
            /></b-button>
          </div>
        </div>

        <transition
          name="matrix-swap"
          mode="out-in"
        >
          <div
            :key="windowStart"
            class="commission-matrix-wrap"
          >
            <table class="commission-matrix">
              <thead>
                <tr>
                  <th
                    class="structure-column"
                    scope="col"
                  >
                    Estrutura / PN
                  </th>
                  <th
                    v-for="installment in visibleInstallments"
                    :key="installment"
                    scope="col"
                  >
                    <span class="installment-label">Parcela</span>
                    <strong>{{ installment }}</strong>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in matrix.rows"
                  :key="row.key"
                >
                  <th
                    class="structure-column"
                    scope="row"
                  >
                    <span class="structure-name">{{ row.roleName }}</span>
                    <span class="consultant-name">
                      <feather-icon
                        icon="UserIcon"
                        size="13"
                      />
                      {{ row.consultantName }}
                    </span>
                  </th>
                  <td
                    v-for="installment in visibleInstallments"
                    :key="`${row.key}-${installment}`"
                  >
                    <button
                      type="button"
                      class="commission-cell"
                      :class="{ 'commission-cell--invoiced': isInvoiced(row.cells[installment]) }"
                      :aria-label="cellAriaLabel(row, installment)"
                      @click="openDetails(row, installment)"
                    >
                      <template v-if="isInvoiced(row.cells[installment])">
                        <span class="cell-status cell-status--invoiced">
                          <feather-icon
                            icon="CheckIcon"
                            size="12"
                          /> Faturada
                        </span>
                        <strong>{{ row.cells[installment].amount | toCurrency }}</strong>
                        <small>{{ row.cells[installment].reconciliation_date | dateTime }}</small>
                      </template>
                      <template v-else>
                        <span class="cell-status cell-status--pending">
                          <feather-icon
                            icon="ClockIcon"
                            size="12"
                          /> 
                          <template v-if="row.cells[installment] && row.cells[installment].reconciliation_date">
                            Pendente
                          </template>
                          <template v-else>
                            Pagamento pendente
                          </template>
                        </span>
                        <strong class="not-invoiced">Comissão não faturada</strong>
                        <small v-if="row.cells[installment] && row.cells[installment].reconciliation_date">
                          {{ row.cells[installment].reconciliation_date | dateTime }}
                        </small>
                        <small v-else>Ver detalhes</small>
                      </template>
                      <feather-icon
                        class="cell-arrow"
                        icon="ChevronRightIcon"
                        size="15"
                      />
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </transition>
      </b-card>
    </template>

    <div
      v-else
      class="commission-empty"
    >
      <span class="commission-empty__icon"><feather-icon
        icon="PercentIcon"
        size="26"
      /></span>
      <h3>Nenhuma comissão encontrada</h3>
      <p>Quando houver comissões vinculadas ao contrato, elas aparecerão aqui por parcela.</p>
    </div>

    <contract-snapshot-history
      v-if="canViewSnapshotHistory"
      class="mt-3"
      :contract-id="$route.params.id"
    />

    <!--
      Os dois históricos são complementares e ficam lado a lado de propósito: acima, o que mudou
      nas linhas de comissão (o VALOR); aqui, o processo de solicitação e decisão.
    -->
    <contract-snapshot-change-requests
      v-if="canViewSnapshotHistory"
      class="mt-3"
      :contract-id="$route.params.id"
    />

    <b-sidebar
      v-model="detailsVisible"
      right
      backdrop
      shadow
      bg-variant="white"
      :text-variant="skin === 'dark' ? 'light' : 'dark'"
      width="410px"
      title="Detalhes da comissão"
      sidebar-class="commission-detail-sidebar"
    >
      <div
        v-if="selectedCell"
        class="commission-detail p-2"
      >
        <div
          class="detail-hero"
          :class="{ 'detail-hero--invoiced': isInvoiced(selectedCell.commission) }"
        >
          <span>Parcela {{ selectedCell.installment }}</span>
          <b-badge :variant="isInvoiced(selectedCell.commission) ? 'light-success' : 'light-warning'">
            {{ isInvoiced(selectedCell.commission) ? 'Comissão faturada' : 'Comissão não faturada' }}
          </b-badge>
          <strong v-if="selectedCell.commission">{{ selectedCell.commission.amount | toCurrency }}</strong>
          <strong v-else>Sem valor registrado</strong>
        </div>
        <dl class="detail-list">
          <div><dt>Estrutura</dt><dd>{{ selectedCell.row.roleName }}</dd></div>
          <div><dt>PN</dt><dd>{{ selectedCell.row.consultantName }}</dd></div>
          <div><dt>Nível de comissionamento</dt><dd>{{ selectedCell.row.roleName }}</dd></div>
          <div><dt>Data de reconciliação</dt><dd>{{ detailDate }}</dd></div>
          <div><dt>Valor reconciliado</dt><dd>{{ detailAmount }}</dd></div>
          <div><dt>Situação</dt><dd>{{ isInvoiced(selectedCell.commission) ? 'Faturada' : 'Comissão não faturada' }}</dd></div>
        </dl>
      </div>
    </b-sidebar>
  </div>
</template>

<script>
import {
  BBadge, BButton, BCard, BSidebar, BSpinner,
} from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import useAppConfig from '@core/app-config/useAppConfig'
import { CONTRACT_RESOURCE, UPDATE_ACTION } from '@/constants/resources'
import { buildCommissionMatrix } from '../utils/contractFinancial'
import ContractSnapshotHistory from '../components/ContractSnapshotHistory.vue'
import ContractSnapshotChangeRequests from '@/modules/snapshot_change/components/ContractSnapshotChangeRequests.vue'
import * as types from '../store/types'

export default {
  components: {
    BBadge,
    BButton,
    BCard,
    BSidebar,
    BSpinner,
    ContractSnapshotHistory,
    ContractSnapshotChangeRequests,
  },
  setup() {
    const { skin } = useAppConfig()
    return { skin, toast: useToast() }
  },
  data() {
    return {
      detailsVisible: false,
      loading: false,
      selectedCell: null,
      windowStart: 0,
      windowWidth: window.innerWidth,
    }
  },
  computed: {
    ...mapGetters({ commissions: types.COMMISSIONS }),
    // Mesma ability já exigida para editar a divisão de comissão.
    canViewSnapshotHistory() {
      return this.$can(UPDATE_ACTION, CONTRACT_RESOURCE)
    },
    items() {
      return this.commissions || []
    },
    matrix() {
      return buildCommissionMatrix(this.items)
    },
    installmentsPerWindow() {
      if (this.windowWidth < 768) return 1
      if (this.windowWidth < 1200) return 2
      return 4
    },
    visibleInstallments() {
      return this.matrix.installments.slice(this.windowStart, this.windowStart + this.installmentsPerWindow)
    },
    isFirstWindow() {
      return this.windowStart === 0
    },
    isLastWindow() {
      return this.windowStart + this.installmentsPerWindow >= this.matrix.installments.length
    },
    rangeLabel() {
      if (!this.matrix.installments.length) return 'Sem parcelas'
      const start = this.windowStart + 1
      const end = Math.min(this.windowStart + this.installmentsPerWindow, this.matrix.installments.length)
      return `Parcelas ${start}–${end} de ${this.matrix.installments.length}`
    },
    detailDate() {
      const date = this.selectedCell && this.selectedCell.commission
        ? this.selectedCell.commission.reconciliation_date
        : null
      return date ? this.$options.filters.dateTime(date) : 'Não informada'
    },
    detailAmount() {
      const commission = this.selectedCell && this.selectedCell.commission
      return commission ? this.$options.filters.toCurrency(commission.amount) : 'Não informado'
    },
  },
  watch: {
    installmentsPerWindow() {
      this.windowStart = Math.min(this.windowStart, this.lastWindowStart())
    },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
    this.get()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    ...mapActions({ getCommissions: types.GET_COMMISSIONS }),
    handleResize() {
      this.windowWidth = window.innerWidth
    },
    isInvoiced(commission) {
      return Boolean(commission && commission.invoice != null && commission.invoice !== '')
    },
    lastWindowStart() {
      const { length } = this.matrix.installments
      return Math.max(0, length - this.installmentsPerWindow)
    },
    previousWindow() {
      this.windowStart = Math.max(0, this.windowStart - this.installmentsPerWindow)
    },
    nextWindow() {
      this.windowStart = Math.min(this.lastWindowStart(), this.windowStart + this.installmentsPerWindow)
    },
    goToFirst() {
      this.windowStart = 0
    },
    goToLast() {
      this.windowStart = this.lastWindowStart()
    },
    openDetails(row, installment) {
      this.selectedCell = { row, installment, commission: row.cells[installment] || null }
      this.detailsVisible = true
    },
    cellAriaLabel(row, installment) {
      const commission = row.cells[installment]
      const status = this.isInvoiced(commission) ? 'faturada' : 'não faturada'
      return `Parcela ${installment}, ${row.roleName}, ${row.consultantName}, comissão ${status}. Abrir detalhes.`
    },
    get() {
      this.loading = true
      this.getCommissions({ id: this.$route.params.id })
        .catch(() => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: 'Oops!',
              text: 'Ocorreu um erro ao buscar as comissões. Entre em contato com o setor de TI.',
              icon: 'AlertTriangleIcon',
              variant: 'danger',
            },
          })
        })
        .finally(() => { this.loading = false })
    },
  },
}
</script>

<style lang="scss" scoped>
.commissions-page { padding-bottom: 1rem; }

.commissions-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin: 0 0.5rem 1rem;
}

.commissions-heading__stats {
  display: flex;
  gap: 0.75rem;
}

.commissions-heading__stats span {
  padding: 0.55rem 0.85rem;
  border-radius: 99px;
  color: #6e6b7b;
  background: rgba(115, 103, 240, 0.08);
}

.commissions-heading__stats strong { color: #7367f0; }

.commission-card {
  overflow: hidden;
  border: 1px solid rgba(110, 107, 123, 0.12);
  border-radius: 0.85rem;
  box-shadow: 0 10px 32px rgba(34, 41, 47, 0.07);
}

.matrix-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(110, 107, 123, 0.12);
}

.matrix-legend,
.matrix-navigation {
  display: flex;
  align-items: center;
  gap: 0.55rem;
}

.matrix-legend span { color: #6e6b7b; font-size: 0.875rem; }
.legend-dot { display: inline-block; width: 8px; height: 8px; margin-right: 0.25rem; border-radius: 50%; }
.legend-dot--invoiced { background: #28c76f; }
.legend-dot--pending { background: #ff9f43; }
.matrix-range { min-width: 145px; color: #6e6b7b; font-size: 0.875rem; text-align: right; }

.commission-matrix-wrap { overflow-x: auto; }
.commission-matrix { width: 100%; table-layout: fixed; border-collapse: separate; border-spacing: 0; }
.commission-matrix th,
.commission-matrix td { width: 210px; padding: 0.75rem; border-right: 1px solid rgba(110, 107, 123, 0.09); border-bottom: 1px solid rgba(110, 107, 123, 0.09); }
.commission-matrix thead th { position: sticky; top: 0; z-index: 2; color: #5e5873; text-align: center; background: #f8f8fb; }
.commission-matrix .structure-column { position: sticky; left: 0; z-index: 3; width: 250px; text-align: left; background: #fff; box-shadow: 8px 0 18px -18px rgba(34, 41, 47, 0.55); }
.commission-matrix thead .structure-column { z-index: 4; background: #f8f8fb; }
.commission-matrix tbody tr:hover .structure-column { background: #faf9ff; }
.installment-label { display: block; color: #b9b9c3; font-size: 0.72rem; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; }
.installment-label + strong { color: #7367f0; font-size: 1.05rem; }
.structure-name { display: block; color: #2f2b3d; font-weight: 700; }
.consultant-name { display: flex; align-items: center; gap: 0.3rem; margin-top: 0.35rem; color: #6e6b7b; font-size: 0.82rem; font-weight: 400; }

.commission-cell {
  position: relative;
  width: 100%;
  min-height: 112px;
  padding: 0.9rem 2rem 0.9rem 0.9rem;
  border: 1px solid rgba(255, 159, 67, 0.2);
  border-radius: 0.7rem;
  color: #2f2b3d;
  text-align: left;
  background: linear-gradient(145deg, rgba(255, 159, 67, 0.08), rgba(255, 255, 255, 0.7));
  transition: transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease;
}

.commission-cell:hover,
.commission-cell:focus-visible { transform: translateY(-2px); border-color: rgba(115, 103, 240, 0.45); box-shadow: 0 8px 20px rgba(34, 41, 47, 0.1); outline: none; }
.commission-cell:focus-visible { box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.2), 0 8px 20px rgba(34, 41, 47, 0.1); }
.commission-cell--invoiced { border-color: rgba(40, 199, 111, 0.2); background: linear-gradient(145deg, rgba(40, 199, 111, 0.09), rgba(255, 255, 255, 0.75)); }
.commission-cell strong,
.commission-cell small { display: block; }
.commission-cell strong { margin: 0.55rem 0 0.2rem; font-size: 1rem; }
.commission-cell small { color: #6e6b7b; }
.commission-cell .not-invoiced { max-width: 135px; font-size: 0.88rem; line-height: 1.2; }
.cell-status { display: inline-flex; align-items: center; gap: 0.25rem; padding: 0.2rem 0.45rem; border-radius: 99px; font-size: 0.7rem; font-weight: 600; }
.cell-status--invoiced { color: #16884a; background: rgba(40, 199, 111, 0.14); }
.cell-status--pending { color: #b65b08; background: rgba(255, 159, 67, 0.16); }
.cell-arrow { position: absolute; top: 50%; right: 0.65rem; color: #b9b9c3; transform: translateY(-50%); }

.commission-empty { display: flex; flex-direction: column; align-items: center; max-width: 480px; margin: 4rem auto; padding: 2rem; color: #6e6b7b; text-align: center; }
.commission-empty__icon { display: grid; place-items: center; width: 58px; height: 58px; margin-bottom: 1rem; border-radius: 18px; color: #7367f0; background: rgba(115, 103, 240, 0.12); }
.commission-empty h3 { color: #2f2b3d; }
.detail-hero { display: flex; flex-direction: column; align-items: flex-start; gap: 0.65rem; padding: 1.25rem; border-radius: 0.8rem; background: rgba(255, 159, 67, 0.1); }
.detail-hero--invoiced { background: rgba(40, 199, 111, 0.1); }
.detail-hero > strong { font-size: 1.7rem; }
.detail-list { margin-top: 1.5rem; }
.detail-list div { display: flex; justify-content: space-between; gap: 1rem; padding: 0.9rem 0; border-bottom: 1px solid rgba(110, 107, 123, 0.12); }
.detail-list dt { color: #6e6b7b; font-weight: 400; }
.detail-list dd { margin: 0; color: #2f2b3d; font-weight: 600; text-align: right; }
.matrix-swap-enter-active,
.matrix-swap-leave-active { transition: opacity 0.14s ease, transform 0.14s ease; }
.matrix-swap-enter { opacity: 0; transform: translateX(8px); }
.matrix-swap-leave-to { opacity: 0; transform: translateX(-8px); }

@media (max-width: 767.98px) {
  .commissions-heading,
  .matrix-toolbar { align-items: stretch; flex-direction: column; }
  .commissions-heading__stats { align-self: flex-start; }
  .matrix-navigation { justify-content: space-between; }
  .matrix-range { margin-right: auto; min-width: 0; text-align: left; }
  .commission-matrix .structure-column { width: 180px; }
  .commission-matrix th,
  .commission-matrix td { width: 190px; }
}

@media (prefers-reduced-motion: reduce) {
  .commission-cell,
  .matrix-swap-enter-active,
  .matrix-swap-leave-active { transition: none; }
}
</style>

<style lang="scss">
.dark-layout {
  .commissions-page {
    .commissions-heading p,
    .matrix-legend span,
    .matrix-range,
    .consultant-name,
    .commission-cell small {
      color: #b4b7bd !important;
    }

    .commissions-heading__stats span {
      color: #c7c9d1;
      background: rgba(115, 103, 240, 0.16);
      box-shadow: inset 0 0 0 1px rgba(115, 103, 240, 0.12);
    }

    .commission-card {
      border-color: rgba(207, 208, 226, 0.1);
      background: #283046;
      box-shadow: 0 12px 34px rgba(10, 13, 22, 0.28);
    }

    .matrix-toolbar {
      border-bottom-color: rgba(207, 208, 226, 0.1);
      background: linear-gradient(90deg, rgba(115, 103, 240, 0.06), transparent);
    }

    .matrix-navigation .btn-flat-secondary {
      color: #c7c9d1;

      &:hover:not(:disabled),
      &:focus {
        color: #fff;
        background: rgba(115, 103, 240, 0.18);
      }
    }

    .commission-matrix th,
    .commission-matrix td {
      border-color: rgba(207, 208, 226, 0.08);
    }

    .commission-matrix thead th,
    .commission-matrix thead .structure-column {
      color: #d0d2d6;
      background: #242b3d;
    }

    .commission-matrix .structure-column {
      background: #283046;
      box-shadow: 10px 0 20px -18px rgba(0, 0, 0, 0.9);
    }

    .commission-matrix tbody tr:hover .structure-column {
      background: #30384d;
    }

    .structure-name,
    .commission-cell {
      color: #f1f1f4;
    }

    .commission-cell {
      border-color: rgba(255, 159, 67, 0.24);
      background: linear-gradient(145deg, rgba(255, 159, 67, 0.13), rgba(36, 43, 61, 0.72));

      &:hover,
      &:focus-visible {
        border-color: rgba(157, 148, 255, 0.62);
        background: linear-gradient(145deg, rgba(115, 103, 240, 0.16), rgba(36, 43, 61, 0.82));
        box-shadow: 0 10px 24px rgba(10, 13, 22, 0.3);
      }
    }

    .commission-cell--invoiced {
      border-color: rgba(40, 199, 111, 0.25);
      background: linear-gradient(145deg, rgba(40, 199, 111, 0.14), rgba(36, 43, 61, 0.72));
    }

    .cell-status--invoiced {
      color: #72e7a5;
      background: rgba(40, 199, 111, 0.16);
    }

    .cell-status--pending {
      color: #ffb976;
      background: rgba(255, 159, 67, 0.17);
    }

    .cell-arrow {
      color: #82868b;
    }

    .commission-empty {
      color: #b4b7bd;
    }

    .commission-empty h3 {
      color: #f1f1f4;
    }
  }

  .commission-detail-sidebar {
    color: #d0d2d6;
    background: #283046 !important;

    > .b-sidebar-body {
      color: #d0d2d6;
      background: linear-gradient(180deg, #283046, #242b3d);
    }

    .b-sidebar-header {
      border-bottom: 1px solid rgba(207, 208, 226, 0.1);
      color: #f1f1f4;
      background: #242b3d;
    }

    .close {
      color: #d0d2d6 !important;
      text-shadow: none;
    }

    .detail-hero {
      background: linear-gradient(145deg, rgba(255, 159, 67, 0.16), rgba(36, 43, 61, 0.78));
    }

    .detail-hero--invoiced {
      background: linear-gradient(145deg, rgba(40, 199, 111, 0.15), rgba(36, 43, 61, 0.78));
    }

    .detail-list div {
      border-bottom-color: rgba(207, 208, 226, 0.1);
    }

    .detail-list dt {
      color: #b4b7bd;
    }

    .detail-list dd {
      color: #f1f1f4;
    }
  }
}
</style>
